import { useTranslation } from 'react-i18next';
import { format, parseISO, addDays, differenceInCalendarDays } from 'date-fns';
import { useDateLocale } from '../../../utils/dateLocale';
import { BedDouble, Icon, Ticket } from '../../ui/Icon';
import type { Booking, Flight, Hotel, Trip } from '../../../types';
import TimelineDay from '../../ui/TimelineDay';
import { FlightTicket } from './FlightTicket';
import { StayTicket } from './StayTicket';
import { ExperienceTicket } from './ExperienceTicket';

interface Props {
  trip: Trip;
  flights: Flight[];
  hotels: Hotel[];
  bookings: Booking[];
  onEditFlight: (f: Flight) => void;
  onDeleteFlight: (id: string) => void;
  onEditHotel: (h: Hotel) => void;
  onDeleteHotel: (id: string) => void;
  onEditBooking: (b: Booking) => void;
  onDeleteBooking: (id: string) => void;
}

/** Every booking laid out day by day along the trip, rather than grouped by kind. */
export function TimelineView({
  trip, flights, hotels, bookings,
  onEditFlight, onDeleteFlight, onEditHotel, onDeleteHotel, onEditBooking, onDeleteBooking,
}: Props) {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const days = Array.from({ length: trip.nights + 1 }, (_, i) =>
    format(addDays(parseISO(trip.startDate), i), 'yyyy-MM-dd'),
  );

  return (
    <div className="space-y-0 mb-6">
      {days.map((dateStr, idx) => {
        const dayFlights  = flights.filter(f => f.departureTime.substring(0, 10) === dateStr);
        const dayHotels   = hotels.filter(h => h.checkIn.substring(0, 10) === dateStr);
        // Nights already under way: checked in earlier, not yet checked out.
        const staying     = hotels.filter(h => h.checkIn.substring(0, 10) < dateStr && h.checkOut.substring(0, 10) >= dateStr);
        const dayBookings = [...bookings.filter(b => b.date.substring(0, 10) === dateStr)]
          .sort((a, b) => (a.startTime ?? '').localeCompare(b.startTime ?? ''));
        // A booking that spans days (a car rental, a multi-day pass) is under way
        // on every day after the one it starts — same treatment as a hotel stay:
        // a pill in the day header rather than a second copy of the ticket.
        const ongoing = bookings.filter(b => {
          const end = b.endDate?.substring(0, 10);
          return !!end && b.date.substring(0, 10) < dateStr && end >= dateStr;
        });
        const hasItems = dayFlights.length > 0 || dayHotels.length > 0 || dayBookings.length > 0;
        // Repeated on every day, a bare title reads as a stuck label: say where in the span we are.
        const ongoingProgress = (b: Booking) => {
          const end = b.endDate!.substring(0, 10);
          if (end === dateStr) return t('bookings.timeline.ongoingEnd');
          const start = parseISO(b.date.substring(0, 10));
          return t('bookings.timeline.ongoingDay', {
            n:     differenceInCalendarDays(parseISO(dateStr), start) + 1,
            total: differenceInCalendarDays(parseISO(end), start) + 1,
          });
        };
        const isLast = idx === days.length - 1;

        return (
          <TimelineDay
            key={dateStr}
            number={idx + 1}
            filled={hasItems}
            last={isLast}
            header={<>
              <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                {t('bookings.timeline.dayLabel', { n: idx + 1 })}
              </span>
              <span className="text-xs text-slate-400 dark:text-slate-500">
                {format(parseISO(dateStr), 'EEE d MMM', { locale: dateLocale })}
              </span>
              {(staying.length > 0 || ongoing.length > 0) && (
                <div className="flex flex-wrap gap-1 ml-1">
                  {staying.map(h => (
                    <span key={h.id} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-violet-50 dark:bg-violet-900/20 text-violet-600 dark:text-violet-400 text-[10px] border border-violet-100 dark:border-violet-800">
                      <Icon as={BedDouble} size={9} />
                      {h.name}
                    </span>
                  ))}
                  {ongoing.map(b => (
                    <span key={b.id} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-sky-50 dark:bg-sky-900/20 text-sky-600 dark:text-sky-400 text-[10px] border border-sky-100 dark:border-sky-800">
                      <Icon as={Ticket} size={9} />
                      {b.title}
                      <span className="opacity-75">· {ongoingProgress(b)}</span>
                    </span>
                  ))}
                </div>
              )}
            </>}
          >
            {hasItems ? (
              <div className="space-y-3">
                {dayFlights.map(f => (
                  <FlightTicket key={f.id} flight={f} docs={f.documents}
                    onEdit={() => onEditFlight(f)}
                    onDelete={() => onDeleteFlight(f.id)}
                  />
                ))}
                {dayHotels.map(h => (
                  <StayTicket key={h.id} hotel={h} docs={h.documents ?? []}
                    onEdit={() => onEditHotel(h)}
                    onDelete={() => onDeleteHotel(h.id)}
                  />
                ))}
                {dayBookings.map(b => (
                  <ExperienceTicket key={b.id} booking={b} docs={b.documents}
                    onEdit={() => onEditBooking(b)}
                    onDelete={() => onDeleteBooking(b.id)}
                  />
                ))}
              </div>
            ) : ongoing.length === 0 && (
              <p className="text-xs text-slate-300 dark:text-slate-600 italic">{t('bookings.timeline.noTickets')}</p>
            )}
          </TimelineDay>
        );
      })}
    </div>
  );
}
